/**
 * SINGLE SOURCE OF TRUTH — Pricing Configuration
 * ─────────────────────────────────────────────────
 * All prices and plan features must be defined here.
 * Every page that shows pricing MUST import from this file.
 * Never hardcode prices in JSX.
 *
 * Plans:
 *   FREE    — 1 monitor, free forever
 *   PRO     — 5 monitors, $49/mo or $490/yr (save 17%)
 *   SCALE   — 50 monitors, $299/mo or $2,490/yr (save ~17%)
 *   ENTERPRISE — custom, contact sales
 *
 * Billing:
 *   ANNUAL_DISCOUNT = 2 months free (pay 10, get 12)
 */

// ─── Base Monthly Prices ─────────────────────────────────────────────────────

export const PLAN_PRICES = {
  FREE: {
    monthly: 0,
    annual_per_month: 0,
    annual_total: 0,
  },
  PRO: {
    monthly: 49,
    annual_per_month: 40,   // ~17% discount  ($490 / 12 = $40.83 rounded to $40)
    annual_total: 490,       // billed once per year
    annual_savings: 98,      // $49*12 - $490 = $588 - $490 = $98
    annual_discount_pct: 17,
  },
  SCALE: {
    monthly: 499,
    annual_per_month: 499,
    annual_total: 499,
    annual_savings: 0,
    annual_discount_pct: 0,
  },
} as const;

// ─── Plan Features ────────────────────────────────────────────────────────────

export const PLAN_FEATURES = {
  FREE: {
    name: "Starter",
    tagline: "For evaluation & side projects",
    monitors: 1,
    ai_fixes_per_month: 5,
    integrations: "All integrations included",
    alerts: ["Slack", "Email"],
    status_pages: 1,
    support: "Community",
    history_days: 7,
    highlights: [
      "1 Active Service Boundary",
      "5 AI Diagnoses / month",
      "Slack + Email Alerts",
      "ChatGPT & Claude MCP Connect",
      "1 Branded Status Page",
    ],
  },
  PRO: {
    name: "Pro",
    tagline: "For growing teams",
    monitors: 5,
    ai_fixes_per_month: 75,
    integrations: "All integrations included",
    alerts: ["Slack", "Email", "Discord", "Telegram", "Teams"],
    status_pages: 25,
    support: "Priority email + Slack",
    history_days: 30,
    highlights: [
      "Everything in Starter, plus:",
      "5 Active Service Boundaries",
      "75 AI Diagnoses / month",
      "Discord, Telegram & Teams Alerts",
      "Autonomous Self-Healing Webhooks",
      "Advanced Routing (Service → Channel)",
      "25 Custom-Domain Status Pages",
    ],
  },
  SCALE: {
    name: "Lifetime Pro",
    tagline: "Lock in UptimeWing forever",
    monitors: 5,
    ai_fixes_per_month: 150,
    integrations: "All integrations included",
    alerts: ["Slack", "Email", "Discord", "Telegram", "Teams", "PagerDuty", "Jira"],
    status_pages: 25,
    support: "lifetime_priority",
    history_days: 90,
    highlights: [
      "Everything in Pro, plus:",
      "5 Active Service Boundaries Forever",
      "150 AI Diagnoses / month Forever",
      "Jira Ticketing & PagerDuty Integration",
      "Lifetime Pro Badge (#XXX)",
      "90 Days History Retention",
    ],
  },
} as const;

// ─── Type Exports ─────────────────────────────────────────────────────────────

export type PlanKey = keyof typeof PLAN_PRICES;
export type BillingCycle = "monthly" | "annual";

/**
 * Get the price to show for a plan/cycle combo.
 */
export function getPlanPrice(plan: Exclude<PlanKey, "FREE">, cycle: BillingCycle): number {
  return cycle === "monthly" ? PLAN_PRICES[plan].monthly : PLAN_PRICES[plan].annual_per_month;
}

/**
 * Get the formatted price string: "$49/mo" or "$40/mo"
 */
export function formatPrice(plan: Exclude<PlanKey, "FREE">, cycle: BillingCycle): string {
  const price = getPlanPrice(plan, cycle);
  return `$${price}/mo`;
}

/**
 * Get the annual total billed string: "Billed annually at $490. Save 17%."
 */
export function getAnnualBillingNote(plan: Exclude<PlanKey, "FREE">): string {
  const { annual_total, annual_savings, annual_discount_pct } = PLAN_PRICES[plan];
  return `Billed annually at $${annual_total}. Save ${annual_discount_pct}% ($${annual_savings}/yr).`;
}

// ─── Legacy Exports (kept for backward compat, derived from PLAN_PRICES) ─────

/** @deprecated Use PLAN_PRICES.PRO.monthly */
export const PRICING = {
  MONTHLY_ORIGINAL: 59.00,
  MONTHLY_PRICE: PLAN_PRICES.PRO.monthly,
  ANNUAL_MONTHS: 12,
  ANNUAL_DISCOUNT_PERCENT: PLAN_PRICES.PRO.annual_discount_pct,
} as const;

/** @deprecated Use PLAN_PRICES.PRO.annual_per_month */
export const annualMonthlyPrice = PLAN_PRICES.PRO.annual_per_month;

/** @deprecated Use PLAN_PRICES.PRO.annual_total */
export const annualTotalPrice = PLAN_PRICES.PRO.annual_total;

/** @deprecated Use PLAN_PRICES.PRO.annual_savings */
export const annualDollarSavings = PLAN_PRICES.PRO.annual_savings;

/** @deprecated  */
export const monthlyDollarSavings = 10;

/** @deprecated Use PLAN_PRICES.PRO.annual_per_month */
export const annualMonthlyStandard = PLAN_PRICES.PRO.monthly;

// ─── Free Tier Limits (keep as-is for auth checks) ───────────────────────────

export const FREE_TIER = {
  MAX_INCIDENTS_PER_MONTH: 5,
  MAX_INTEGRATIONS_EACH: 9999,
  CHANNELS: ["slack", "email"],
  MAX_HISTORY_DAYS: 7,
  SUPPORT: "community",
} as const;

export const PRO_TIER = {
  MAX_INCIDENTS_PER_MONTH: 50,
  MAX_INTEGRATIONS_EACH: 9999,
  CHANNELS: ["slack", "email"],
  MAX_HISTORY_DAYS: 30,
  SUPPORT: "priority_email_slack",
} as const;

export const TEAM_TIER = {
  MAX_INCIDENTS_PER_MONTH: 500,
  MAX_INTEGRATIONS_EACH: 9999,
  CHANNELS: ["slack", "email", "pagerduty"],
  MAX_HISTORY_DAYS: 365,
  SUPPORT: "dedicated_slack",
} as const;
